// src/views/admin/theme/ThemePage.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/inputs/input';
import { useForm, Controller } from 'react-hook-form';
import { getSettingService, updateSettingService } from '@/services/SettingServices';
import validationErrors from '@/lib/validationErrors';
import { useDispatch } from 'react-redux';
import Card from '@/components/ui/cards/Card';
import { fileUpload, getFileUrl } from '@/services/CommonService';
import CardHeader from '@/components/ui/cards/CardHeader';
import CardBody from '@/components/ui/cards/CardBody';
import ToastMe from '@/components/ui/ToastMe';
import PermissionCheck from '@/components/common/PermissionCheck';

const ThemePage = ({ pageTitle }) => {
  const dispatch = useDispatch();
  const {
    setValue,
    getValues,
    formState: { errors },
    handleSubmit,
    control,
    reset,
  } = useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [isFormSumbmit, setIsFormSumbmit] = useState(false);
  const filedList = ['theme_logo', 'fav_icon', 'footer_logo'];

  useEffect(() => {
    getDataByKey({ key_list: filedList });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formReset = async () => {
    try {
      const formData = getValues();
      for (const key in formData) await setValue(key, '');
      await reset();
    } catch (error) {
      console.error('Error in onReset:', error);
    }
  };

  const setPreviewUrl = (key, fileName) => {
    // add cache-buster so <img> refreshes immediately
    const url = `${getFileUrl(fileName, 'theme')}?v=${Date.now()}`;
    setValue(`${key}_url`, url, { shouldDirty: true });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      ToastMe('File is not valid', 'error');
      return;
    }
    if (!file.type?.includes('image/')) {
      ToastMe('Please upload only image files', 'error');
      return;
    }

    dispatch(fileUpload({ file }, 'theme'))
      .then((res) => {
        const out = res?.data;
        if (!out?.fileName) return;

        // map temp field to actual key
        const fieldName = event.target?.name; // temp_theme_logo | temp_fav_icon | temp_footer_logo
        if (fieldName === 'temp_theme_logo') {
          setValue('theme_logo', out.fileName, { shouldDirty: true });
          setPreviewUrl('theme_logo', out.fileName);
        } else if (fieldName === 'temp_fav_icon') {
          setValue('fav_icon', out.fileName, { shouldDirty: true });
          setPreviewUrl('fav_icon', out.fileName);
        } else if (fieldName === 'temp_footer_logo') {
          setValue('footer_logo', out.fileName, { shouldDirty: true });
          setPreviewUrl('footer_logo', out.fileName);
        }
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
      });
  };

  const getDataByKey = (keyList) => {
    setIsLoading(true);
    dispatch(getSettingService(keyList))
      .then((res) => {
        const formData = res?.data?.data || {};
        for (const key in formData) {
          const val = formData[key] || '';
          setValue(key, val);
          if (val) setPreviewUrl(key, val);
        }
        setIsLoading(false);
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsLoading(false);
      });
  };

  const onSubmit = (data) => {
    setIsFormSumbmit(true);
    
    // Filter out preview URLs and temp fields - only send the actual setting keys
    const cleanData = {
      theme_logo: data.theme_logo || '',
      fav_icon: data.fav_icon || '',
      footer_logo: data.footer_logo || '',
    };
    
    // Remove empty values to avoid overwriting existing settings
    Object.keys(cleanData).forEach(key => {
      if (!cleanData[key]) {
        delete cleanData[key];
      }
    });
    
    dispatch(updateSettingService(cleanData))
      .then(() => {
        getDataByKey({ key_list: filedList });
        setIsFormSumbmit(false);
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsFormSumbmit(false);
      });
  };

  return (
    <Card>
      <CardHeader isBorder={true}>
        <h3 className="db-card-title">{pageTitle}</h3>
      </CardHeader>
      <CardBody>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Theme Logo */}
            <div>
              <Controller
                control={control}
                name="temp_theme_logo"
                render={({ field }) => (
                  <Input
                    {...field}
                    labelStyleClassName="db-field-title required"
                    size="sm"
                    labelText="Logo (128px,43px)"
                    onChange={handleFileUpload}
                    type="file"
                    id="temp_theme_logo"
                    errorType={errors?.temp_theme_logo?.type}
                  />
                )}
              />
              <div className="image-preview-box size-32 border rounded-xl p-4 mt-3">
                <img src={getValues('theme_logo_url')} alt="Theme Logo" className="object-contain object-center w-full h-full" />
              </div>
            </div>

            {/* Fav Icon */}
            <div>
              <Controller
                control={control}
                name="temp_fav_icon"
                render={({ field }) => (
                  <Input
                    {...field}
                    labelStyleClassName="db-field-title required"
                    size="sm"
                    labelText="Fav Icon (120px,120px)"
                    onChange={handleFileUpload}
                    type="file"
                    id="temp_fav_icon"
                    errorType={errors?.temp_fav_icon?.type}
                  />
                )}
              />
              <div className="image-preview-box size-32 border rounded-xl p-4 mt-3">
                <img src={getValues('fav_icon_url')} alt="Fav Icon" className="object-contain object-center w-full h-full" />
              </div>
            </div>

            {/* Footer Logo */}
            <div>
              <Controller
                control={control}
                name="temp_footer_logo"
                render={({ field }) => (
                  <Input
                    {...field}
                    labelStyleClassName="db-field-title required"
                    size="sm"
                    labelText="Footer Logo (144px,48px)"
                    onChange={handleFileUpload}
                    type="file"
                    id="temp_footer_logo"
                    errorType={errors?.temp_footer_logo?.type}
                  />
                )}
              />
              <div className="image-preview-box size-32 border rounded-xl p-4 mt-3">
                <img src={getValues('footer_logo_url')} alt="Footer Logo" className="object-contain object-center w-full h-full" />
              </div>
            </div>

            <PermissionCheck permsission={'update_settings'}>
              <div className="sm:col-span-2">
                <button type="submit" className="db-btn text-white bg-primary" disabled={isFormSumbmit || isLoading}>
                  <i className="lab lab-save" />
                  <span>Save</span>
                  {isFormSumbmit && (
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                </button>
              </div>
            </PermissionCheck>
          </div>
        </form>
      </CardBody>
    </Card>
  );
};

export default ThemePage;
